import { makeAutoObservable, runInAction } from 'mobx';
import partnerServiceService from '../services/partnerServiceService';

class PartnerServiceStore {
  services = [];
  currentService = null;
  loading = false;
  error = null;

  // Filters
  searchQuery = '';
  categoryFilter = null;
  activeFilter = null; // true, false, null (all)

  constructor(rootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  // Computed - Filtered services
  get filteredServices() {
    let filtered = this.services;

    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase();
      filtered = filtered.filter(service =>
        service.name_en?.toLowerCase().includes(query) ||
        service.name_el?.toLowerCase().includes(query)
      );
    }

    if (this.categoryFilter) {
      filtered = filtered.filter(service => service.service_category === this.categoryFilter);
    }

    if (this.activeFilter !== null) {
      filtered = filtered.filter(service => service.is_active === this.activeFilter);
    }

    return filtered;
  }

  // Actions - Filters
  setSearchQuery(query) {
    this.searchQuery = query;
  }

  setCategoryFilter(category) {
    this.categoryFilter = category;
  }

  setActiveFilter(active) {
    this.activeFilter = active;
  }

  clearFilters() {
    this.searchQuery = '';
    this.categoryFilter = null;
    this.activeFilter = null;
  }

  // Actions - Services CRUD
  async fetchServices() {
    this.loading = true;
    this.error = null;

    const result = await partnerServiceService.getServices();

    runInAction(() => {
      if (result.success) {
        this.services = result.data;
      } else {
        this.error = result.error;
      }
      this.loading = false;
    });

    return result;
  }

  async fetchServiceById(serviceId) {
    this.loading = true;
    this.error = null;

    const result = await partnerServiceService.getServiceById(serviceId);

    runInAction(() => {
      if (result.success) {
        this.currentService = result.data;
      } else {
        this.error = result.error;
      }
      this.loading = false;
    });

    return result;
  }

  async createService(data) {
    this.loading = true;
    this.error = null;

    const result = await partnerServiceService.createService(data);

    runInAction(() => {
      if (result.success) {
        this.services.push(result.data);
      } else {
        this.error = result.error;
      }
      this.loading = false;
    });

    return result;
  }

  async updateService(serviceId, data) {
    this.loading = true;
    this.error = null;

    const result = await partnerServiceService.updateService(serviceId, data);

    runInAction(() => {
      if (result.success) {
        const index = this.services.findIndex(s => s.id === serviceId);
        if (index !== -1) {
          this.services[index] = result.data;
        }
        if (this.currentService?.id === serviceId) {
          this.currentService = result.data;
        }
      } else {
        this.error = result.error;
      }
      this.loading = false;
    });

    return result;
  }

  async deleteService(serviceId) {
    this.loading = true;
    this.error = null;

    const result = await partnerServiceService.deleteService(serviceId);

    runInAction(() => {
      if (result.success) {
        this.services = this.services.filter(s => s.id !== serviceId);
        if (this.currentService?.id === serviceId) {
          this.currentService = null;
        }
      } else {
        this.error = result.error;
      }
      this.loading = false;
    });

    return result;
  }

  // Actions - Availability Management
  async addAvailability(serviceId, data) {
    this.loading = true;
    this.error = null;

    const result = await partnerServiceService.addAvailability(serviceId, data);

    runInAction(() => {
      if (result.success) {
        // Refresh current service to get updated availabilities
        if (this.currentService?.id === serviceId) {
          this.fetchServiceById(serviceId);
        }
      } else {
        this.error = result.error;
      }
      this.loading = false;
    });

    return result;
  }

  async updateAvailability(serviceId, availabilityId, data) {
    this.loading = true;
    this.error = null;

    const result = await partnerServiceService.updateAvailability(serviceId, availabilityId, data);

    runInAction(() => {
      if (result.success) {
        if (this.currentService?.id === serviceId) {
          this.fetchServiceById(serviceId);
        }
      } else {
        this.error = result.error;
      }
      this.loading = false;
    });

    return result;
  }

  async deleteAvailability(serviceId, availabilityId) {
    this.loading = true;
    this.error = null;

    const result = await partnerServiceService.deleteAvailability(serviceId, availabilityId);

    runInAction(() => {
      if (result.success) {
        if (this.currentService?.id === serviceId) {
          this.fetchServiceById(serviceId);
        }
      } else {
        this.error = result.error;
      }
      this.loading = false;
    });

    return result;
  }

  async copyAvailability(serviceId, sourceDay, targetDays) {
    this.error = null;

    const result = await partnerServiceService.copyAvailability(serviceId, sourceDay, targetDays);

    runInAction(() => {
      if (result.success) {
        if (this.currentService?.id === serviceId) {
          this.fetchServiceById(serviceId);
        }
      } else {
        this.error = result.error;
      }
    });

    return result;
  }

  // Actions - Holidays Management
  async addHoliday(serviceId, data) {
    this.loading = true;
    this.error = null;

    const result = await partnerServiceService.addHoliday(serviceId, data);

    runInAction(() => {
      if (result.success) {
        if (this.currentService?.id === serviceId) {
          this.fetchServiceById(serviceId);
        }
      } else {
        this.error = result.error;
      }
      this.loading = false;
    });

    return result;
  }

  async deleteHoliday(serviceId, holidayId) {
    this.loading = true;
    this.error = null;

    const result = await partnerServiceService.deleteHoliday(serviceId, holidayId);

    runInAction(() => {
      if (result.success) {
        if (this.currentService?.id === serviceId) {
          this.fetchServiceById(serviceId);
        }
      } else {
        this.error = result.error;
      }
      this.loading = false;
    });

    return result;
  }

  // Reset
  reset() {
    this.services = [];
    this.currentService = null;
    this.loading = false;
    this.error = null;
    this.clearFilters();
  }
}

export default PartnerServiceStore;
